<!-- TestRunner.vue -->
<template>
	<teleport to="body">
		<div
			class="test-runner"
			:class="{ 'test-runner--no-copy': settings.preventCopy }"
			@copy.prevent="handleCopyAttempt"
			@cut.prevent="handleCopyAttempt"
			@paste.prevent="handleCopyAttempt"
			@contextmenu="handleContextMenu"
			@selectstart="handleSelectStart"
		>
			<!-- Оверлей -->
			<div class="test-runner__overlay" @click="handleCloseClick"></div>

			<!-- Контейнер теста -->
			<div class="test-runner__container">
				<!-- Шапка с таймером -->
				<div class="test-runner__header">
					<div class="test-runner__header-left">
						<h2 class="test-runner__title">{{ testData.title }}</h2>
						<p v-if="testData.description" class="test-runner__subtitle">
							{{ testData.description }}
						</p>
						<!-- Режим пробного прохождения -->
						<div class="test-runner__mode-badge">
							<span class="mode-badge__text">Пробное прохождение</span>
							<span v-if="maxAttempts > 1" class="mode-badge__attempts">
								(попытка {{ attemptNumber }}/{{ maxAttempts }})
							</span>
						</div>
					</div>
					<div class="test-runner__header-right">
						<!-- Общий таймер теста -->
						<div
							v-if="testData.duration"
							class="test-runner__timer"
							:class="{ 'test-runner__timer--warning': timeWarning }"
						>
							<span class="timer-icon">⏱️</span>
							<span class="timer-time">{{ formattedTime }}</span>
						</div>

						<!-- Таймер на вопрос (только если включен) -->
						<div
							v-if="settings.timePerQuestion"
							class="test-runner__question-timer"
							:class="{
								'test-runner__question-timer--warning': questionTimeWarning,
								'test-runner__question-timer--critical': questionTimeCritical,
							}"
						>
							<span class="timer-icon">❓</span>
							<span class="timer-time">{{ formattedQuestionTime }}</span>
						</div>

						<button
							type="button"
							class="test-runner__close-btn"
							@click="handleCloseClick"
							title="Закрыть"
						>
							✕
						</button>
					</div>
				</div>

				<!-- Индикатор активных настроек -->
				<div class="test-runner__settings-bar">
					<div class="settings-badges">
						<span
							v-if="settings.randomOrder"
							class="setting-badge"
							title="Случайный порядок вопросов"
						>
							Случайный порядок 🔀
						</span>
						<span
							v-if="settings.penalizeWrong"
							class="setting-badge setting-badge--warning"
							title="Штраф за ошибки"
						>
							⚠️ Штраф {{ settings.penaltyPercentage }}%
						</span>
						<span
							v-if="!settings.allowReview"
							class="setting-badge setting-badge--info"
							title="Нельзя вернуться к вопросам"
						>
							Без возврата 🔒
						</span>
						<span
							v-if="!settings.showCorrectAnswers"
							class="setting-badge"
							title="Ответы не показываются"
						>
							Без ответов 👁️‍🗨️
						</span>
						<span
							v-if="settings.preventCopy"
							class="setting-badge setting-badge--warning"
							title="Копирование запрещено"
						>
							Копирование запрещено 🚫
						</span>
						<span
							v-if="settings.timePerQuestion"
							class="setting-badge setting-badge--info"
							title="Ограничение времени на вопрос"
						>
							⏱️ {{ settings.timePerQuestion }} сек/вопрос
						</span>
					</div>
				</div>

				<!-- Основной контент -->
				<div class="test-runner__body">
					<!-- Левая панель - Информация о баллах -->
					<div class="test-runner__sidebar test-runner__sidebar--left">
						<div class="score-panel">
							<div class="score-panel__passing">
								<div class="score-panel__label">Проходной балл:</div>
								<div class="score-panel__value">
									{{ testData.passingScore }}%
								</div>
							</div>

							<div class="score-panel__question-points">
								<div class="score-panel__label">Баллы за текущий вопрос:</div>
								<div class="score-panel__value score-panel__value--large">
									{{ currentQuestionPoints }}
								</div>
								<div
									v-if="settings.penalizeWrong"
									class="score-panel__penalty-info"
								>
									Штраф за ошибку: -{{ formattedCurrentPenalty }}
								</div>
							</div>

							<!-- Таймер вопроса в боковой панели (только если включен) -->
							<div
								v-if="settings.timePerQuestion"
								class="score-panel__question-timer"
							>
								<div class="score-panel__label">Время на вопрос:</div>
								<div
									class="score-panel__timer-value"
									:class="{
										'score-panel__timer-value--warning': questionTimeWarning,
										'score-panel__timer-value--critical': questionTimeCritical,
									}"
								>
									{{ formattedQuestionTime }}
								</div>
								<div class="score-panel__timer-bar">
									<div
										class="score-panel__timer-fill"
										:style="{ width: questionTimePercentage + '%' }"
										:class="{
											'score-panel__timer-fill--warning': questionTimeWarning,
											'score-panel__timer-fill--critical': questionTimeCritical,
										}"
									></div>
								</div>
							</div>
						</div>
					</div>

					<!-- Центральная панель - Вопрос -->
					<div class="test-runner__main">
						<div class="question-container">
							<!-- Прогресс (если включен) -->
							<div v-if="settings.showProgressBar" class="question-progress">
								<div class="question-progress__text">
									Вопрос {{ currentQuestionIndex + 1 }} из
									{{ displayedQuestions.length }}
								</div>
								<div class="question-progress__bar">
									<div
										class="question-progress__fill"
										:style="{ width: `${questionProgress}%` }"
									></div>
								</div>
							</div>

							<!-- Индикатор без прогресс-бара -->
							<div v-else class="question-counter">
								Вопрос {{ currentQuestionIndex + 1 }} из
								{{ displayedQuestions.length }}
							</div>

							<!-- Сам вопрос -->
							<div class="question-content">
								<h3 class="question-content__text">
									{{ currentQuestion.text }}
								</h3>

								<!-- Изображение вопроса -->
								<div
									v-if="currentQuestion.image"
									class="question-content__image"
								>
									<img :src="currentQuestion.image" alt="Question image" />
								</div>

								<!-- Тип вопроса -->
								<div class="question-content__type">
									<span v-if="currentQuestion.type === 'single'">
										Выберите один правильный ответ 📍
									</span>
									<span v-else-if="currentQuestion.type === 'multiple'">
										Выберите все правильные ответы ☑️
									</span>
									<span v-else>Введите ответ ✏️</span>
								</div>

								<!-- Варианты ответов -->
								<div class="question-answers">
									<!-- Single choice -->
									<div
										v-if="currentQuestion.type === 'single'"
										class="answer-list"
									>
										<label
											v-for="(answer, index) in currentQuestionAnswers"
											:key="answer.id"
											class="answer-item"
											:class="{
												'answer-item--selected': isAnswerSelected(answer.id),
												'answer-item--disabled': isAnswerLocked,
											}"
										>
											<input
												type="radio"
												:name="`question-${currentQuestion.id}`"
												:value="answer.id"
												v-model="userAnswers[currentQuestion.id]"
												:disabled="isAnswerLocked"
												class="answer-item__radio"
											/>
											<div class="answer-item__content">
												<span class="answer-item__marker">{{
													String.fromCharCode(65 + index)
												}}</span>
												<span class="answer-item__text">{{ answer.text }}</span>
											</div>
											<div v-if="answer.image" class="answer-item__image">
												<img :src="answer.image" alt="Answer image" />
											</div>
										</label>
									</div>

									<!-- Multiple choice -->
									<div
										v-else-if="currentQuestion.type === 'multiple'"
										class="answer-list"
									>
										<label
											v-for="(answer, index) in currentQuestionAnswers"
											:key="answer.id"
											class="answer-item"
											:class="{
												'answer-item--selected': isAnswerSelectedMultiple(
													answer.id
												),
												'answer-item--disabled': isAnswerLocked,
											}"
										>
											<input
												type="checkbox"
												:value="answer.id"
												v-model="userAnswers[currentQuestion.id]"
												:disabled="isAnswerLocked"
												class="answer-item__checkbox"
											/>
											<div class="answer-item__content">
												<span class="answer-item__marker">{{
													String.fromCharCode(65 + index)
												}}</span>
												<span class="answer-item__text">{{ answer.text }}</span>
											</div>
											<div v-if="answer.image" class="answer-item__image">
												<img :src="answer.image" alt="Answer image" />
											</div>
										</label>
									</div>

									<!-- Text input -->
									<div v-else class="answer-text-input">
										<input
											type="text"
											v-model="userAnswers[currentQuestion.id]"
											:disabled="isAnswerLocked"
											class="answer-text-input__field"
											placeholder="Введите ваш ответ..."
											@keyup.enter="nextQuestion"
										/>
										<div
											v-if="currentQuestion.caseSensitive"
											class="answer-text-input__hint"
										>
											⚠️ Ответ чувствителен к регистру
										</div>
									</div>
								</div>

								<!-- Предупреждение о невозможности вернуться -->
								<div
									v-if="!settings.allowReview && !isAnswerLocked"
									class="question-warning"
								>
									<span class="question-warning__icon">⚠️</span>
									<span class="question-warning__text">
										После перехода к следующему вопросу вы не сможете изменить
										ответ
									</span>
								</div>

								<!-- Предупреждение о времени на вопрос -->
								<div
									v-if="settings.timePerQuestion && questionTimeCritical"
									class="question-warning question-warning--critical"
								>
									<span class="question-warning__icon">⏰</span>
									<span class="question-warning__text">
										Время на вопрос заканчивается!
									</span>
								</div>
							</div>

							<!-- Навигация внизу -->
							<div class="question-navigation">
								<!-- Кнопка "Назад" только если разрешён просмотр -->
								<button
									v-if="settings.allowReview"
									type="button"
									class="btn btn--secondary"
									@click="previousQuestion"
									:disabled="currentQuestionIndex === 0"
								>
									← Назад
								</button>
								<div v-else class="navigation-spacer"></div>

								<button
									v-if="currentQuestionIndex < displayedQuestions.length - 1"
									type="button"
									class="btn btn--primary"
									@click="nextQuestion"
									:disabled="
										!settings.allowReview && !isCurrentQuestionAnswered
									"
								>
									Вперед →
								</button>

								<button
									v-else
									type="button"
									class="btn btn--success"
									@click="finishTest()"
								>
									Завершить тест
								</button>
							</div>
						</div>
					</div>

					<!-- Правая панель - Навигация по вопросам -->
					<div class="test-runner__sidebar test-runner__sidebar--right">
						<div class="navigation-panel">
							<h3 class="navigation-panel__title">Навигация</h3>

							<div class="navigation-panel__grid">
								<button
									v-for="(question, index) in displayedQuestions"
									:key="question.id"
									type="button"
									class="nav-question-btn"
									:class="{
										'nav-question-btn--current': index === currentQuestionIndex,
										'nav-question-btn--answered': isQuestionAnswered(
											question.id
										),
										'nav-question-btn--locked':
											!settings.allowReview && index < currentQuestionIndex,
										'nav-question-btn--skipped': isQuestionSkipped(question.id),
										'nav-question-btn--correct':
											showResults && isQuestionCorrect(question.id),
										'nav-question-btn--wrong':
											showResults &&
											!isQuestionCorrect(question.id) &&
											isQuestionAnswered(question.id),
									}"
									@click="goToQuestion(index)"
									:disabled="!canNavigateToQuestion(index)"
									:title="getQuestionButtonTitle(index, question)"
								>
									{{ index + 1 }}
									<span
										v-if="!settings.allowReview && index < currentQuestionIndex"
										class="nav-question-btn__lock"
									>
										🔒
									</span>
									<span
										v-if="isQuestionSkipped(question.id)"
										class="nav-question-btn__skipped"
									>
										⏭️
									</span>
								</button>
							</div>

							<div class="navigation-panel__legend">
								<div class="legend-item">
									<span
										class="legend-item__dot legend-item__dot--current"
									></span>
									<span class="legend-item__text">Текущий</span>
								</div>
								<div class="legend-item">
									<span
										class="legend-item__dot legend-item__dot--answered"
									></span>
									<span class="legend-item__text">Отвечен</span>
								</div>
								<div class="legend-item">
									<span
										class="legend-item__dot legend-item__dot--unanswered"
									></span>
									<span class="legend-item__text">Не отвечен</span>
								</div>
								<div v-if="!settings.allowReview" class="legend-item">
									<span
										class="legend-item__dot legend-item__dot--locked"
									></span>
									<span class="legend-item__text">Заблокирован</span>
								</div>
								<div v-if="settings.timePerQuestion" class="legend-item">
									<span
										class="legend-item__dot legend-item__dot--skipped"
									></span>
									<span class="legend-item__text">Пропущен (время)</span>
								</div>
							</div>

							<button
								type="button"
								class="btn btn--large btn--warning navigation-panel__finish"
								@click="finishTest()"
							>
								Завершить тест 🏁
							</button>
						</div>
					</div>
				</div>
			</div>

			<!-- Уведомление о попытке копирования -->
			<transition name="fade">
				<div v-if="showCopyWarning" class="copy-warning-toast">
					<span class="copy-warning-toast__icon">🚫</span>
					<span class="copy-warning-toast__text"
						>Копирование запрещено во время теста!</span
					>
				</div>
			</transition>

			<!-- Модальное окно результатов -->
			<div v-if="showResults" class="results-modal" @click.self="closeResults">
				<div class="results-modal__content">
					<div
						class="results-modal__header"
						:class="{
							'results-modal__header--success': isPassed,
							'results-modal__header--failure': !isPassed,
						}"
					>
						<h2 class="results-modal__title">
							{{ isPassed ? 'Поздравляем! 🎉' : 'Тест не пройден 😞' }}
						</h2>
						<button
							type="button"
							class="results-modal__close"
							@click="closeResults"
						>
							✕
						</button>
					</div>

					<div class="results-modal__body">
						<!-- Основные результаты -->
						<div class="results-summary">
							<div class="results-summary__score">
								<div class="results-summary__label">Ваш результат:</div>
								<div
									class="results-summary__value"
									:class="{
										'results-summary__value--success': isPassed,
										'results-summary__value--failure': !isPassed,
									}"
								>
									{{ scorePercentage }}%
								</div>
								<div class="results-summary__points">
									{{ currentScore }} из {{ displayedMaxPoints }} баллов
								</div>

								<!-- Информация о штрафах (только если включено) -->
								<div
									v-if="settings.penalizeWrong && penaltyPoints > 0"
									class="results-summary__penalty"
								>
									<span class="penalty-badge"
										>⚠️ Штраф за ошибки: -{{ penaltyPoints }} баллов</span
									>
								</div>

								<!-- Информация о пропущенных по времени -->
								<div
									v-if="skippedQuestions.size > 0"
									class="results-summary__skipped"
								>
									<span class="skipped-badge"
										>⏰ Пропущено по времени:
										{{ skippedQuestions.size }} вопросов</span
									>
								</div>
							</div>

							<div class="results-summary__stats">
								<div class="stat-card stat-card--success">
									<div class="stat-card__value">{{ correctAnswers }}</div>
									<div class="stat-card__label">Правильно</div>
									<div v-if="earnedPoints > 0" class="stat-card__points">
										+{{ earnedPoints }}
									</div>
								</div>
								<div class="stat-card stat-card--danger">
									<div class="stat-card__value">{{ wrongAnswers }}</div>
									<div class="stat-card__label">Неправильно</div>
									<div
										v-if="settings.penalizeWrong && penaltyPoints > 0"
										class="stat-card__points stat-card__points--negative"
									>
										-{{ penaltyPoints }}
									</div>
								</div>
								<div class="stat-card stat-card--neutral">
									<div class="stat-card__value">{{ unansweredCount }}</div>
									<div class="stat-card__label">Пропущено</div>
								</div>
							</div>

							<div class="results-summary__passing">
								<div class="results-summary__passing-label">
									Проходной балл: {{ testData.passingScore }}%
								</div>
								<div
									class="results-summary__passing-status"
									:class="{
										'results-summary__passing-status--pass': isPassed,
										'results-summary__passing-status--fail': !isPassed,
									}"
								>
									{{ isPassed ? '✓ Тест пройден' : '✗ Тест не пройден' }}
								</div>
							</div>
						</div>

						<!-- Детальные результаты по вопросам (если разрешено) -->
						<div v-if="settings.showCorrectAnswers" class="results-details">
							<h3 class="results-details__title">Детальные результаты</h3>

							<div class="results-details__list">
								<div
									v-for="(question, index) in displayedQuestions"
									:key="question.id"
									class="result-item"
									:class="{
										'result-item--correct': isQuestionCorrect(question.id),
										'result-item--wrong':
											!isQuestionCorrect(question.id) &&
											isQuestionAnswered(question.id),
										'result-item--unanswered': !isQuestionAnswered(question.id),
										'result-item--skipped': isQuestionSkipped(question.id),
									}"
								>
									<div class="result-item__header">
										<span class="result-item__number"
											>Вопрос {{ index + 1 }}</span
										>
										<span class="result-item__status">
											{{ getQuestionStatus(question.id) }}
										</span>
										<span class="result-item__points">
											{{ getQuestionEarnedPoints(question) }} /
											{{ getQuestionMaxPoints(question) }}
											<span
												v-if="
													settings.penalizeWrong &&
													getQuestionPenalty(question) > 0
												"
												class="result-item__penalty"
											>
												(-{{ getQuestionPenalty(question) }})
											</span>
										</span>
									</div>

									<div class="result-item__question">{{ question.text }}</div>

									<!-- Показываем ответы только если разрешено -->
									<div
										v-if="question.type !== 'text'"
										class="result-item__answers"
									>
										<div
											v-for="answer in question.answers"
											:key="answer.id"
											class="result-answer"
											:class="{
												'result-answer--correct': answer.isCorrect,
												'result-answer--user': isUserAnswer(
													question.id,
													answer.id
												),
												'result-answer--wrong':
													isUserAnswer(question.id, answer.id) &&
													!answer.isCorrect,
											}"
										>
											<span class="result-answer__icon">
												<template v-if="answer.isCorrect">✓</template>
												<template
													v-else-if="isUserAnswer(question.id, answer.id)"
													>✗</template
												>
											</span>
											{{ answer.text }}
										</div>
									</div>

									<div v-else class="result-item__text-answer">
										<div class="text-answer-row">
											<strong>Ваш ответ:</strong>
											<span
												:class="{
													'text-answer--wrong': !isQuestionCorrect(question.id),
												}"
											>
												{{ userAnswers[question.id] || 'Не отвечено' }}
											</span>
										</div>
										<div class="text-answer-row">
											<strong>Правильный ответ:</strong>
											<span class="text-answer--correct">{{
												question.correctText
											}}</span>
										</div>
									</div>

									<div
										v-if="question.explanation"
										class="result-item__explanation"
									>
										<strong>💡 Объяснение:</strong> {{ question.explanation }}
									</div>
								</div>
							</div>
						</div>

						<!-- Сообщение если ответы не показываются -->
						<div v-else class="results-no-answers">
							<div class="results-no-answers__icon">👁️‍🗨️</div>
							<p class="results-no-answers__text">
								Просмотр правильных ответов отключён для этого теста
							</p>
						</div>
					</div>

					<div class="results-modal__footer">
						<button
							v-if="hasAttemptsLeft"
							type="button"
							class="btn btn--large btn--primary"
							@click="retryTest"
						>
							Пройти заново (попытка {{ attemptNumber + 1 }} из
							{{ maxAttempts }})
						</button>
						<div v-else class="attempts-exhausted">
							<span class="attempts-exhausted__icon">🚫</span>
							<span class="attempts-exhausted__text"
								>Попытки исчерпаны ({{ attemptNumber }} из
								{{ maxAttempts }})</span
							>
						</div>

						<button
							type="button"
							class="btn btn--large btn--secondary"
							@click="closeResults"
						>
							Закрыть
						</button>
					</div>
				</div>
			</div>
		</div>
	</teleport>
</template>

<script>
export default {
	name: 'TestRunner',
	props: {
		testData: {
			type: Object,
			required: true,
		},
		questions: {
			type: Array,
			required: true,
		},
	},
	emits: ['close'],
	data() {
		return {
			currentQuestionIndex: 0,
			userAnswers: {},
			answeredQuestions: new Set(),
			lockedQuestions: new Set(),
			skippedQuestions: new Set(), // Вопросы пропущенные по времени
			showResults: false,
			startedAt: null,
			finishedAt: null,
			totalTimeSeconds: 0,

			// Таймер теста
			timeRemaining: null,
			timerInterval: null,

			// Таймер вопроса
			questionTimeRemaining: null,
			questionTimerInterval: null,

			displayedQuestions: [],
			originalQuestionOrder: [],
			attemptNumber: 1,
			isFinishing: false,

			// Уведомление о копировании
			showCopyWarning: false,
			copyWarningTimeout: null,
		}
	},
	computed: {
		/**
		 * КЛЮЧЕВОЙ COMPUTED: Настройки с правильной обработкой props
		 * Все настройки работают ТОЛЬКО если соответствующий чекбокс включен
		 */
		settings() {
			// Логируем входящие данные для отладки
			console.log('📋 testData получен:', {
				penalizeWrong: this.testData.penalizeWrong,
				disableCopying: this.testData.disableCopying,
				limitTimePerQuestion: this.testData.limitTimePerQuestion,
				timePerQuestion: this.testData.timePerQuestion,
			})

			return {
				selectRandomQuestions: this.testData.selectRandomQuestions ?? false,
				questionsToShow: this.testData.questionsToShow ?? null,
				questionDistributionMode:
					this.testData.questionDistributionMode ?? 'random',
				questionTypeDistribution: this.testData.questionTypeDistribution ?? {},
				// Базовые настройки
				randomOrder: this.testData.randomOrder === true,
				showCorrectAnswers: this.testData.showCorrectAnswers !== false, // по умолчанию true
				allowReview: this.testData.allowReview !== false, // по умолчанию true
				showProgressBar: this.testData.showProgressBar !== false, // по умолчанию true

				// Штраф за ошибки - ТОЛЬКО если чекбокс penalizeWrong === true
				penalizeWrong: this.testData.penalizeWrong === true,
				penaltyPercentage:
					this.testData.penalizeWrong === true
						? this.testData.penaltyPercentage || 50
						: 0,

				// Запрет копирования - ТОЛЬКО если чекбокс disableCopying === true
				preventCopy: this.testData.disableCopying === true,

				// Время на вопрос - ТОЛЬКО если чекбокс limitTimePerQuestion === true И указано время
				timePerQuestion:
					this.testData.limitTimePerQuestion === true
						? this.testData.timePerQuestion || 60
						: null,
			}
		},

		currentQuestion() {
			return this.displayedQuestions[this.currentQuestionIndex] || {}
		},

		currentQuestionAnswers() {
			if (!this.currentQuestion.answers) return []
			return this.currentQuestion.answers
		},

		currentQuestionPoints() {
			return (
				this.currentQuestion.customPoints ||
				this.testData.pointsPerQuestion ||
				1
			)
		},

		// Штраф за текущий вопрос (только если включен penalizeWrong)
		currentQuestionPenalty() {
			if (!this.settings.penalizeWrong) return 0
			return (
				(this.currentQuestionPoints * this.settings.penaltyPercentage) / 100
			)
		},

		formattedCurrentPenalty() {
			const penalty = this.currentQuestionPenalty
			if (penalty === 0) return '0'
			if (Number.isInteger(penalty)) return String(penalty)
			// Убираем лишние нули: 0.10 → 0.1
			return parseFloat(penalty.toFixed(2)).toString()
		},

		isAnswerLocked() {
			if (this.settings.allowReview) return false
			return this.lockedQuestions.has(this.currentQuestion.id)
		},

		isCurrentQuestionAnswered() {
			return this.isQuestionAnswered(this.currentQuestion.id)
		},

		questionProgress() {
			return Math.round(
				((this.currentQuestionIndex + 1) / this.displayedQuestions.length) * 100
			)
		},

		// Форматирование времени теста
		formattedTime() {
			if (this.timeRemaining === null) return '--:--'
			const minutes = Math.floor(this.timeRemaining / 60)
			const seconds = this.timeRemaining % 60
			return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
				2,
				'0'
			)}`
		},

		timeWarning() {
			if (!this.testData.duration || this.timeRemaining === null) return false
			const warningTime = this.testData.duration * 60 * 0.2
			return this.timeRemaining <= warningTime
		},

		// Форматирование времени вопроса
		formattedQuestionTime() {
			if (this.questionTimeRemaining === null) return '--'
			const minutes = Math.floor(this.questionTimeRemaining / 60)
			const seconds = this.questionTimeRemaining % 60
			if (minutes > 0) {
				return `${minutes}:${String(seconds).padStart(2, '0')}`
			}
			return `${seconds}`
		},

		// Процент оставшегося времени на вопрос
		questionTimePercentage() {
			if (!this.settings.timePerQuestion || this.questionTimeRemaining === null)
				return 100
			return Math.round(
				(this.questionTimeRemaining / this.settings.timePerQuestion) * 100
			)
		},

		// Предупреждение о времени на вопрос (меньше 30%)
		questionTimeWarning() {
			if (!this.settings.timePerQuestion || this.questionTimeRemaining === null)
				return false
			return this.questionTimeRemaining <= this.settings.timePerQuestion * 0.3
		},

		// Критическое время на вопрос (меньше 10%)
		questionTimeCritical() {
			if (!this.settings.timePerQuestion || this.questionTimeRemaining === null)
				return false
			return this.questionTimeRemaining <= this.settings.timePerQuestion * 0.1
		},

		// Заработанные баллы (без штрафов)
		earnedPoints() {
			let points = 0
			this.displayedQuestions.forEach(question => {
				if (this.isQuestionCorrect(question.id)) {
					points +=
						question.customPoints || this.testData.pointsPerQuestion || 1
				}
			})
			return points
		},

		displayedQuestionCount() {
			return this.displayedQuestions.length
		},

		displayedMaxPoints() {
			return this.displayedQuestions.reduce((sum, question) => {
				const points = question.customPoints || this.testData.pointsPerQuestion || 1
				return sum + points
			}, 0)
		},

		// Штрафные баллы (только если penalizeWrong === true)
		penaltyPoints() {
			// Если штрафы отключены - возвращаем 0
			if (!this.settings.penalizeWrong) return 0

			let penalty = 0
			this.displayedQuestions.forEach(question => {
				if (
					this.isQuestionAnswered(question.id) &&
					!this.isQuestionCorrect(question.id)
				) {
					const questionPoints =
						question.customPoints || this.testData.pointsPerQuestion || 1
					penalty += Math.round(
						(questionPoints * this.settings.penaltyPercentage) / 100
					)
				}
			})
			return penalty
		},

		currentScore() {
			const score = this.earnedPoints - this.penaltyPoints
			return Math.max(0, score)
		},

		scorePercentage() {
			if (!this.displayedMaxPoints) return 0
			return Math.round((this.currentScore / this.displayedMaxPoints) * 100)
		},

		correctAnswers() {
			return this.displayedQuestions.filter(q => this.isQuestionCorrect(q.id))
				.length
		},

		wrongAnswers() {
			return this.displayedQuestions.filter(
				q => this.isQuestionAnswered(q.id) && !this.isQuestionCorrect(q.id)
			).length
		},

		unansweredCount() {
			return this.displayedQuestions.filter(q => !this.isQuestionAnswered(q.id))
				.length
		},

		isPassed() {
			return this.scorePercentage >= (this.testData.passingScore || 0)
		},

		maxAttempts() {
			return this.testData.attempts || 1
		},

		hasAttemptsLeft() {
			return this.attemptNumber < this.maxAttempts
		},
	},

	watch: {
		// Следим за сменой вопроса для сброса таймера
		currentQuestionIndex() {
			// Сбрасываем таймер вопроса ТОЛЬКО если timePerQuestion включен
			if (this.settings.timePerQuestion) {
				this.resetQuestionTimer()
			}
		},
	},

	mounted() {
		this.initTest()
		document.body.style.overflow = 'hidden'
		document.documentElement.style.overflow = 'hidden'

		// Добавляем глобальные обработчики ТОЛЬКО если preventCopy включен
		if (this.settings.preventCopy) {
			this.setupCopyPrevention()
			console.log('🔒 Защита от копирования АКТИВИРОВАНА')
		} else {
			console.log('🔓 Защита от копирования ОТКЛЮЧЕНА')
		}
	},

	beforeUnmount() {
		this.clearTimer()
		this.clearQuestionTimer()
		document.body.style.overflow = ''
		document.documentElement.style.overflow = ''

		// Удаляем обработчики копирования
		this.removeCopyPrevention()

		if (this.copyWarningTimeout) {
			clearTimeout(this.copyWarningTimeout)
		}
	},

	methods: {
		// ============================================
		// ИНИЦИАЛИЗАЦИЯ
		// ============================================
		initTest() {
			this.prepareQuestions()
			this.initializeAnswers()
			this.currentQuestionIndex = 0
			this.lockedQuestions = new Set()
			this.skippedQuestions = new Set()
			this.showResults = false
			this.startedAt = new Date()
			this.finishedAt = null
			this.totalTimeSeconds = 0

			// Запускаем таймер теста
			if (this.testData.duration) {
				this.timeRemaining = this.testData.duration * 60
				this.startTimer()
			}

			// Запускаем таймер вопроса ТОЛЬКО если timePerQuestion включен
			if (this.settings.timePerQuestion) {
				this.resetQuestionTimer()
				console.log(
					`⏱️ Таймер вопроса АКТИВИРОВАН: ${this.settings.timePerQuestion} сек`
				)
			} else {
				console.log('⏱️ Таймер вопроса ОТКЛЮЧЕН')
			}

			console.log('🎯 Тест инициализирован с настройками:', this.settings)
		},

		prepareQuestions() {
			// Сохраняем оригинальный порядок
			this.originalQuestionOrder = [...this.questions]

			let questionsPool = [...this.questions]

			// Если включен случайный выбор из пула
			if (
				this.settings.selectRandomQuestions &&
				this.settings.questionsToShow
			) {
				questionsPool = this.selectQuestionsFromPool(questionsPool)
				console.log(`🎲 Выбрано ${questionsPool.length} вопросов из пула`)
			}

			// Применяем случайный порядок если нужно
			if (this.settings.randomOrder) {
				questionsPool = this.shuffleArray(questionsPool)
				console.log('🔀 Вопросы перемешаны')
			}

			this.displayedQuestions = questionsPool
		},

		/**
		 * Выбирает N вопросов из пула согласно настройкам распределения
		 */
		selectQuestionsFromPool(pool) {
			const targetCount = Math.min(
				this.settings.questionsToShow || pool.length,
				pool.length
			)

			const mode = this.settings.questionDistributionMode || 'random'
			const distribution = this.settings.questionTypeDistribution || {}

			// Режим "Случайное" - просто выбираем N случайных
			if (mode === 'random') {
				return this.shuffleArray([...pool]).slice(0, targetCount)
			}

			// Группируем вопросы по типам
			const byType = {
				single: pool.filter(q => q.type === 'single'),
				multiple: pool.filter(q => q.type === 'multiple'),
				text: pool.filter(q => q.type === 'text'),
			}

			const result = []
			const usedIds = new Set()

			if (mode === 'proportional') {
				// Режим "По процентам"
				const types = ['single', 'multiple', 'text']

				types.forEach(type => {
					const percentage = distribution[type] || 0
					if (percentage <= 0) return

					const countForType = Math.round((targetCount * percentage) / 100)
					const available = this.shuffleArray([...byType[type]])
					const selected = available.slice(0, countForType)

					selected.forEach(q => {
						result.push(q)
						usedIds.add(q.id)
					})
				})
			} else if (mode === 'fixed') {
				// Режим "Фиксированное количество"
				const types = ['single', 'multiple', 'text']

				types.forEach(type => {
					const countForType = distribution[type] || 0
					if (countForType <= 0) return

					const available = this.shuffleArray([...byType[type]])
					const selected = available.slice(0, countForType)

					selected.forEach(q => {
						result.push(q)
						usedIds.add(q.id)
					})
				})
			}

			// Если не набрали нужное количество - добираем случайными
			if (result.length < targetCount) {
				const remaining = pool.filter(q => !usedIds.has(q.id))
				const shuffledRemaining = this.shuffleArray([...remaining])
				const needed = targetCount - result.length

				shuffledRemaining.slice(0, needed).forEach(q => {
					result.push(q)
				})
			}

			// Перемешиваем финальный результат и обрезаем до нужного количества
			return this.shuffleArray(result).slice(0, targetCount)
		},

		initializeAnswers() {
			this.userAnswers = {}
			this.displayedQuestions.forEach(q => {
				if (q.type === 'multiple') {
					this.userAnswers[q.id] = []
				} else {
					this.userAnswers[q.id] = null
				}
			})
		},

		shuffleArray(array) {
			const shuffled = [...array]
			for (let i = shuffled.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1))
				;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
			}
			return shuffled
		},

		// ============================================
		// ТАЙМЕР ТЕСТА
		// ============================================
		startTimer() {
			this.timerInterval = setInterval(() => {
				this.timeRemaining--

				if (this.timeRemaining <= 0) {
					this.clearTimer()
					this.finishTest(true)
				}
			}, 1000)
		},

		clearTimer() {
			if (this.timerInterval) {
				clearInterval(this.timerInterval)
				this.timerInterval = null
			}
		},

		// ============================================
		// ТАЙМЕР ВОПРОСА (работает ТОЛЬКО если включен)
		// ============================================
		resetQuestionTimer() {
			this.clearQuestionTimer()

			// Проверяем, что таймер вопроса включен
			if (!this.settings.timePerQuestion) {
				console.log('⏱️ resetQuestionTimer: таймер отключен')
				return
			}

			this.questionTimeRemaining = this.settings.timePerQuestion
			this.startQuestionTimer()
			console.log(
				`⏱️ Таймер вопроса запущен: ${this.settings.timePerQuestion} сек`
			)
		},

		startQuestionTimer() {
			// Дополнительная проверка
			if (!this.settings.timePerQuestion) return

			this.questionTimerInterval = setInterval(() => {
				this.questionTimeRemaining--

				if (this.questionTimeRemaining <= 0) {
					this.handleQuestionTimeout()
				}
			}, 1000)
		},

		clearQuestionTimer() {
			if (this.questionTimerInterval) {
				clearInterval(this.questionTimerInterval)
				this.questionTimerInterval = null
			}
		},

		handleQuestionTimeout() {
			this.clearQuestionTimer()

			// Отмечаем вопрос как пропущенный по времени
			this.skippedQuestions.add(this.currentQuestion.id)

			// Блокируем вопрос в режиме без возврата
			if (!this.settings.allowReview) {
				this.lockedQuestions.add(this.currentQuestion.id)
			}

			console.log(`⏰ Время на вопрос ${this.currentQuestionIndex + 1} истекло`)

			// Переходим к следующему вопросу или завершаем тест
			if (this.currentQuestionIndex < this.displayedQuestions.length - 1) {
				this.currentQuestionIndex++
				// Таймер сбросится автоматически через watcher
			} else {
				// Это был последний вопрос - завершаем тест
				this.finishTest(true)
			}
		},

		// ============================================
		// ПРЕДОТВРАЩЕНИЕ КОПИРОВАНИЯ (работает ТОЛЬКО если включено)
		// ============================================
		setupCopyPrevention() {
			// Блокируем горячие клавиши
			document.addEventListener('keydown', this.handleKeyDown)

			// Блокируем drag
			document.addEventListener('dragstart', this.handleDragStart)
		},

		removeCopyPrevention() {
			document.removeEventListener('keydown', this.handleKeyDown)
			document.removeEventListener('dragstart', this.handleDragStart)
		},

		handleKeyDown(e) {
			// Проверяем, что защита включена
			if (!this.settings.preventCopy) return

			// Блокируем Ctrl+C, Ctrl+X, Ctrl+V, Ctrl+A, Ctrl+P
			if (e.ctrlKey || e.metaKey) {
				const blockedKeys = ['c', 'x', 'v', 'a', 'p']
				if (blockedKeys.includes(e.key.toLowerCase())) {
					e.preventDefault()
					this.showCopyNotification()
					console.log(
						`🚫 Заблокирована комбинация: Ctrl+${e.key.toUpperCase()}`
					)
				}
			}

			// Блокируем PrintScreen
			if (e.key === 'PrintScreen') {
				e.preventDefault()
				this.showCopyNotification()
				console.log('🚫 Заблокирован PrintScreen')
			}
		},

		handleDragStart(e) {
			if (this.settings.preventCopy) {
				e.preventDefault()
			}
		},

		handleCopyAttempt(e) {
			// Проверяем, что защита включена
			if (this.settings.preventCopy) {
				e.preventDefault()
				this.showCopyNotification()
				console.log('🚫 Попытка копирования заблокирована')
			}
		},

		handleContextMenu(e) {
			// Проверяем, что защита включена
			if (this.settings.preventCopy) {
				e.preventDefault()
				this.showCopyNotification()
				console.log('🚫 Контекстное меню заблокировано')
			}
		},

		handleSelectStart(e) {
			// Проверяем, что защита включена
			// Разрешаем выделение в полях ввода
			if (
				this.settings.preventCopy &&
				e.target.tagName !== 'INPUT' &&
				e.target.tagName !== 'TEXTAREA'
			) {
				e.preventDefault()
			}
		},

		showCopyNotification() {
			if (this.copyWarningTimeout) {
				clearTimeout(this.copyWarningTimeout)
			}

			this.showCopyWarning = true

			this.copyWarningTimeout = setTimeout(() => {
				this.showCopyWarning = false
			}, 2000)
		},

		// ============================================
		// ПРОВЕРКА ОТВЕТОВ
		// ============================================
		isAnswerSelected(answerId) {
			return this.userAnswers[this.currentQuestion.id] === answerId
		},

		isAnswerSelectedMultiple(answerId) {
			const answer = this.userAnswers[this.currentQuestion.id]
			return Array.isArray(answer) && answer.includes(answerId)
		},

		isQuestionAnswered(questionId) {
			const answer = this.userAnswers[questionId]
			if (Array.isArray(answer)) {
				return answer.length > 0
			}
			return answer !== null && answer !== undefined && answer !== ''
		},

		isQuestionSkipped(questionId) {
			return this.skippedQuestions.has(questionId)
		},

		isQuestionCorrect(questionId) {
			const question = this.displayedQuestions.find(q => q.id === questionId)
			if (!question) return false

			const userAnswer = this.userAnswers[questionId]

			if (!this.isQuestionAnswered(questionId)) return false

			if (question.type === 'single') {
				const correctAnswer = question.answers?.find(a => a.isCorrect)
				return correctAnswer && userAnswer === correctAnswer.id
			}

			if (question.type === 'multiple') {
				const correctIds =
					question.answers
						?.filter(a => a.isCorrect)
						.map(a => a.id)
						.sort() || []
				const userIds = Array.isArray(userAnswer) ? [...userAnswer].sort() : []
				return JSON.stringify(correctIds) === JSON.stringify(userIds)
			}

			if (question.type === 'text') {
				const correct = (question.correctText || '').trim()
				const user = String(userAnswer || '').trim()

				if (question.caseSensitive) {
					return correct === user
				}
				return correct.toLowerCase() === user.toLowerCase()
			}

			return false
		},

		isUserAnswer(questionId, answerId) {
			const userAnswer = this.userAnswers[questionId]
			if (Array.isArray(userAnswer)) {
				return userAnswer.includes(answerId)
			}
			return userAnswer === answerId
		},

		// ============================================
		// РАСЧЁТ БАЛЛОВ
		// ============================================
		getQuestionMaxPoints(question) {
			return question.customPoints || this.testData.pointsPerQuestion || 1
		},

		getQuestionEarnedPoints(question) {
			if (this.isQuestionCorrect(question.id)) {
				return this.getQuestionMaxPoints(question)
			}
			return 0
		},

		getQuestionPenalty(question) {
			// Штраф только если penalizeWrong включен
			if (!this.settings.penalizeWrong) return 0
			if (!this.isQuestionAnswered(question.id)) return 0
			if (this.isQuestionCorrect(question.id)) return 0

			const maxPoints = this.getQuestionMaxPoints(question)
			return Math.round((maxPoints * this.settings.penaltyPercentage) / 100)
		},

		getQuestionStatus(questionId) {
			if (
				this.isQuestionSkipped(questionId) &&
				!this.isQuestionAnswered(questionId)
			) {
				return 'Пропущен (время) ⏰'
			}
			if (this.isQuestionCorrect(questionId)) {
				return 'Верно ✓'
			}
			if (this.isQuestionAnswered(questionId)) {
				return 'Неверно ✗'
			}
			return 'Не отвечено ⭕'
		},

		// ============================================
		// НАВИГАЦИЯ
		// ============================================
		canNavigateToQuestion(index) {
			if (this.settings.allowReview) return true
			return index >= this.currentQuestionIndex
		},

		getQuestionButtonTitle(index, question) {
			if (this.isQuestionSkipped(question.id)) {
				return `Вопрос ${index + 1} (пропущен по времени)`
			}
			if (!this.settings.allowReview && index < this.currentQuestionIndex) {
				return `Вопрос ${index + 1} (заблокирован)`
			}

			const answered = this.isQuestionAnswered(question.id) ? ' - отвечен' : ''
			return `Вопрос ${index + 1}${answered}`
		},

		nextQuestion() {
			if (!this.settings.allowReview && this.isCurrentQuestionAnswered) {
				this.lockedQuestions.add(this.currentQuestion.id)
			}

			if (this.currentQuestionIndex < this.displayedQuestions.length - 1) {
				this.currentQuestionIndex++
			}
		},

		previousQuestion() {
			if (!this.settings.allowReview) return

			if (this.currentQuestionIndex > 0) {
				this.currentQuestionIndex--
			}
		},

		goToQuestion(index) {
			if (!this.canNavigateToQuestion(index)) return

			if (!this.settings.allowReview && index > this.currentQuestionIndex) {
				if (this.isCurrentQuestionAnswered) {
					this.lockedQuestions.add(this.currentQuestion.id)
				}
			}

			this.currentQuestionIndex = index
		},

		// ============================================
		// ЗАВЕРШЕНИЕ ТЕСТА
		// ============================================
		finishTest(autoFinish = false) {
			if (this.isFinishing) return

			this.isFinishing = true

			try {
				this.clearTimer()
				this.clearQuestionTimer()

				// Приводим к boolean
				const isAutoFinish = autoFinish === true

				const unanswered = this.unansweredCount

				if (!isAutoFinish && unanswered > 0) {
					const message = `У вас ${unanswered} ${this.pluralize(
						unanswered,
						'неотвеченный вопрос',
						'неотвеченных вопроса',
						'неотвеченных вопросов'
					)}. Завершить тест?`

					if (!confirm(message)) {
						if (this.testData.duration && this.timeRemaining > 0) {
							this.startTimer()
						}
						if (this.settings.timePerQuestion) {
							this.startQuestionTimer()
						}
						return
					}
				}

				if (isAutoFinish) {
					alert('Время вышло! Тест завершён автоматически.')
				}

					this.finishedAt = new Date()
					if (this.startedAt) {
						this.totalTimeSeconds = Math.max(
							0,
							Math.round((this.finishedAt - this.startedAt) / 1000)
						)
					}
					this.showResults = true

				console.log('📊 Результаты теста:', {
					score: this.currentScore,
					percentage: this.scorePercentage,
					correct: this.correctAnswers,
					wrong: this.wrongAnswers,
					unanswered: this.unansweredCount,
					skipped: this.skippedQuestions.size,
					penalty: this.penaltyPoints,
					passed: this.isPassed,
					settings: this.settings,
				})
			} finally {
				this.isFinishing = false
			}
		},

		buildResultsPayload() {
			const maxPoints = this.displayedMaxPoints || this.testData.totalPoints || 0
			return {
				score: this.currentScore,
				percentage: this.scorePercentage,
				correct: this.correctAnswers,
				wrong: this.wrongAnswers,
				unanswered: this.unansweredCount,
				skipped: this.skippedQuestions.size,
				penalty: this.penaltyPoints,
				passed: this.isPassed,
				maxPoints,
				questionCount: this.displayedQuestionCount,
				questionPoolCount: this.questions.length,
				attemptsUsed: this.attemptNumber,
				attemptsLimit: this.maxAttempts,
				startTime: this.startedAt ? this.startedAt.toISOString() : null,
				endTime: this.finishedAt ? this.finishedAt.toISOString() : null,
				totalTime: this.totalTimeSeconds,
			}
		},

		closeResults() {
			this.showResults = false
			const answersCopy = JSON.parse(JSON.stringify(this.userAnswers || {}))
			const questionIds = Array.isArray(this.displayedQuestions)
				? this.displayedQuestions.map(q => q.id)
				: []
			answersCopy._questionIds = questionIds
			this.$emit('close', {
				results: this.buildResultsPayload(),
				answers: answersCopy,
			})
		},

		retryTest() {
			if (!this.hasAttemptsLeft) {
				return
			}

			this.attemptNumber++
			this.showResults = false
			this.clearTimer()
			this.clearQuestionTimer()
			this.initTest()
		},

		handleCloseClick() {
			if (this.showResults) {
				this.closeResults()
				return
			}

			if (
				confirm(
					'Вы уверены, что хотите выйти из теста? Прогресс не будет сохранен.'
				)
			) {
				this.clearTimer()
				this.clearQuestionTimer()
				this.$emit('close', null)
			}
		},

		// ============================================
		// УТИЛИТЫ
		// ============================================
		pluralize(number, one, few, many) {
			const n = Math.abs(number) % 100
			const n1 = n % 10

			if (n > 10 && n < 20) return many
			if (n1 > 1 && n1 < 5) return few
			if (n1 === 1) return one
			return many
		},
	},
}
</script>

<style scoped>
/* ============================================
   ЗАЩИТА ОТ КОПИРОВАНИЯ (применяется условно)
   ============================================ */
.test-runner--no-copy {
	-webkit-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	user-select: none;
	-webkit-touch-callout: none;
}

/* Разрешаем выделение в полях ввода */
.test-runner--no-copy input,
.test-runner--no-copy textarea {
	-webkit-user-select: text;
	-moz-user-select: text;
	-ms-user-select: text;
	user-select: text;
}

/* Уведомление о копировании */
.copy-warning-toast {
	position: fixed;
	top: 100px;
	left: 50%;
	transform: translateX(-50%);
	background: linear-gradient(135deg, #f44336 0%, #e91e63 100%);
	color: white;
	padding: 16px 32px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	gap: 12px;
	box-shadow: 0 8px 32px rgba(244, 67, 54, 0.4);
	z-index: 300000;
	font-weight: 600;
	font-size: 16px;
}

.copy-warning-toast__icon {
	font-size: 24px;
}

.fade-enter-active,
.fade-leave-active {
	transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
	transform: translateX(-50%) translateY(-20px);
}

/* ============================================
   ТАЙМЕР ВОПРОСА
   ============================================ */
.test-runner__question-timer {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 20px;
	background: rgba(255, 255, 255, 0.2);
	border-radius: 8px;
	font-size: 18px;
	font-weight: 700;
	min-width: 80px;
	justify-content: center;
	transition: all 0.3s;
}

.test-runner__question-timer--warning {
	background: rgba(255, 152, 0, 0.9);
}

.test-runner__question-timer--critical {
	background: rgba(244, 67, 54, 0.9);
	animation: pulse-critical 0.5s infinite;
}

@keyframes pulse-critical {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.08);
	}
}

/* Таймер в боковой панели */
.score-panel__question-timer {
	padding: 16px;
	background: #fff;
	border-radius: 8px;
	margin-top: 16px;
	text-align: center;
}

.score-panel__timer-value {
	font-size: 32px;
	font-weight: 700;
	color: #667eea;
	margin: 8px 0;
	transition: color 0.3s;
}

.score-panel__timer-value--warning {
	color: #ff9800;
}

.score-panel__timer-value--critical {
	color: #f44336;
	animation: blink 0.5s infinite;
}

@keyframes blink {
	0%,
	100% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
}

.score-panel__timer-bar {
	height: 8px;
	background: #e0e0e0;
	border-radius: 4px;
	overflow: hidden;
	margin-top: 8px;
}

.score-panel__timer-fill {
	height: 100%;
	background: #667eea;
	transition: width 1s linear, background 0.3s;
	border-radius: 4px;
}

.score-panel__timer-fill--warning {
	background: #ff9800;
}

.score-panel__timer-fill--critical {
	background: #f44336;
}

/* Пропущенные вопросы */
.nav-question-btn--skipped {
	background: #fff3e0 !important;
	border-color: #ff9800 !important;
	color: #e65100 !important;
}

.nav-question-btn__skipped {
	position: absolute;
	font-size: 10px;
	top: 2px;
	right: 2px;
}

.legend-item__dot--skipped {
	background: #fff3e0;
	border-color: #ff9800;
}

/* Предупреждение о времени */
.question-warning--critical {
	background: #ffebee;
	border-color: #f44336;
	animation: shake 0.5s infinite;
}

@keyframes shake {
	0%,
	100% {
		transform: translateX(0);
	}
	25% {
		transform: translateX(-2px);
	}
	75% {
		transform: translateX(2px);
	}
}

/* Результаты - пропущенные */
.result-item--skipped {
	border-color: #ff9800;
	background: #fff8e1;
}

.results-summary__skipped {
	margin-top: 12px;
}

.skipped-badge {
	display: inline-block;
	padding: 6px 12px;
	background: #fff3e0;
	color: #e65100;
	border-radius: 16px;
	font-size: 14px;
	font-weight: 500;
}

/* Иконка таймера */
.timer-icon {
	font-size: 18px;
}

/* ============================================
   ОСТАЛЬНЫЕ СТИЛИ
   ============================================ */

/* Бейдж режима */
.test-runner__mode-badge {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-top: 8px;
	padding: 4px 12px;
	background: rgba(255, 255, 255, 0.2);
	border-radius: 12px;
	font-size: 12px;
}

.mode-badge__icon {
	font-size: 14px;
}

/* Панель настроек */
.test-runner__settings-bar {
	padding: 8px 24px;
	background: #f8f9fa;
	border-bottom: 1px solid #e0e0e0;
}

.settings-badges {
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
}

.setting-badge {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 4px 10px;
	background: #e3f2fd;
	color: #1976d2;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 500;
}

.setting-badge--warning {
	background: #fff3e0;
	color: #e65100;
}

.setting-badge--info {
	background: #f3e5f5;
	color: #7b1fa2;
}

/* Информация о штрафе */
.score-panel__penalty-info {
	font-size: 12px;
	color: #e65100;
	margin-top: 4px;
}

/* Счётчик вопросов (без прогресс-бара) */
.question-counter {
	text-align: center;
	font-size: 16px;
	font-weight: 600;
	color: #667eea;
	margin-bottom: 24px;
	padding: 12px;
	background: #f5f5f5;
	border-radius: 8px;
}

/* Предупреждение в вопросе */
.question-warning {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 16px;
	padding: 12px 16px;
	background: #fff3e0;
	border: 1px solid #ffcc02;
	border-radius: 8px;
	font-size: 13px;
	color: #e65100;
}

.question-warning__icon {
	font-size: 18px;
}

/* Заблокированный ответ */
.answer-item--disabled {
	opacity: 0.7;
	cursor: not-allowed;
	pointer-events: none;
}

/* Заблокированная кнопка навигации */
.nav-question-btn--locked {
	background: #f5f5f5;
	border-color: #e0e0e0;
	color: #9e9e9e;
	cursor: not-allowed;
	position: relative;
}

.nav-question-btn__lock {
	position: absolute;
	font-size: 10px;
	bottom: 2px;
	right: 2px;
}

/* Легенда для заблокированных */
.legend-item__dot--locked {
	background: #f5f5f5;
	border-color: #bdbdbd;
	position: relative;
}

.legend-item__dot--locked::after {
	content: '🔒';
	position: absolute;
	font-size: 8px;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
}

/* Спейсер для навигации */
.navigation-spacer {
	width: 100px;
}

/* Подсказка для текстового ввода */
.answer-text-input__hint {
	margin-top: 8px;
	font-size: 12px;
	color: #e65100;
}

/* Результаты - штраф */
.results-summary__penalty {
	margin-top: 12px;
}

.penalty-badge {
	display: inline-block;
	padding: 6px 12px;
	background: #ffebee;
	color: #c62828;
	border-radius: 16px;
	font-size: 14px;
	font-weight: 500;
}

/* Баллы в карточках статистики */
.stat-card__points {
	margin-top: 4px;
	font-size: 14px;
	opacity: 0.9;
}

.stat-card__points--negative {
	color: #ffcdd2;
}

/* Штраф в результатах вопроса */
.result-item__penalty {
	color: #c62828;
	font-size: 12px;
}

/* Если ответы не показываются */
.results-no-answers {
	text-align: center;
	padding: 40px 20px;
	background: #f5f5f5;
	border-radius: 12px;
	margin-top: 24px;
}

.results-no-answers__icon {
	font-size: 48px;
	margin-bottom: 16px;
	opacity: 0.5;
}

.results-no-answers__text {
	font-size: 16px;
	color: #666;
	margin: 0;
}

/* Заголовок результатов с цветом */
.results-modal__header--success {
	background: linear-gradient(135deg, #4caf50 0%, #8bc34a 100%);
}

.results-modal__header--failure {
	background: linear-gradient(135deg, #f44336 0%, #e91e63 100%);
}

/* Основные стили */
.test-runner {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 10000099999;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

.test-runner,
.test-runner * {
	font-family: 'Poppins', sans-serif !important;
}

.test-runner__overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.87);
	z-index: 100001;
}

.test-runner__container {
	position: fixed;
	inset: 10px;
	width: auto;
	height: auto;
	background: #fff;
	border-radius: 16px;
	display: flex;
	flex-direction: column;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
	overflow: hidden;
	z-index: 100002;
}

/* Шапка */
.test-runner__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px 32px;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	color: #fff;
}

.test-runner__header-left {
	flex: 1;
}

.test-runner__title {
	margin: 0 0 4px 0;
	font-size: 24px;
	font-weight: 700;
}

.test-runner__subtitle {
	margin: 0;
	font-size: 14px;
	opacity: 0.9;
}

.test-runner__header-right {
	display: flex;
	align-items: center;
	gap: 12px;
}

.test-runner__timer {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 20px;
	background: rgba(255, 255, 255, 0.2);
	border-radius: 8px;
	font-size: 20px;
	font-weight: 700;
	min-width: 120px;
	justify-content: center;
}

.test-runner__timer--warning {
	background: rgba(255, 152, 0, 0.9);
	animation: pulse 1s infinite;
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.05);
	}
}

.test-runner__close-btn {
	width: 40px;
	height: 40px;
	background: rgba(255, 255, 255, 0.2);
	border: none;
	border-radius: 50%;
	color: #fff;
	font-size: 24px;
	cursor: pointer;
	transition: all 0.2s;
	display: flex;
	align-items: center;
	justify-content: center;
}

.test-runner__close-btn:hover {
	background: rgba(255, 255, 255, 0.3);
	transform: rotate(90deg);
}

/* Основное тело */
.test-runner__body {
	flex: 1;
	display: flex;
	/* Avoid nested scrollbars: sidebars/main handle their own scroll */
	overflow: hidden;
}

/* Боковые панели */
.test-runner__sidebar {
	width: 300px;
	padding: 24px;
	background: #f9f9f9;
	overflow-y: auto;
}

.test-runner__sidebar--left {
	border-right: 1px solid #e0e0e0;
}

.test-runner__sidebar--right {
	border-left: 1px solid #e0e0e0;
}

/* Панель баллов */
.score-panel__title {
	margin: 0 0 20px 0;
	font-size: 18px;
	font-weight: 700;
	color: #333;
	text-align: center;
}

.score-panel__current {
	text-align: center;
	margin-bottom: 16px;
}

.score-panel__label {
	font-size: 13px;
	color: #666;
	margin-bottom: 8px;
}

.score-panel__value {
	font-size: 32px;
	font-weight: 700;
	color: #667eea;
}

.score-panel__value--large {
	font-size: 48px;
}

.score-panel__passing,
.score-panel__question-points {
	padding: 16px;
	background: #fff;
	border-radius: 8px;
	margin-bottom: 16px;
	text-align: center;
}

/* Центральная панель */
.test-runner__main {
	flex: 1;
	padding: 32px;
	overflow-y: auto;
	background: #fff;
}

.question-container {
	max-width: 800px;
	margin: 0 auto;
}

.question-progress {
	margin-bottom: 32px;
}

.question-progress__text {
	font-size: 16px;
	font-weight: 600;
	color: #667eea;
	margin-bottom: 12px;
	text-align: center;
}

.question-progress__bar {
	height: 8px;
	background: #e0e0e0;
	border-radius: 4px;
	overflow: hidden;
}

.question-progress__fill {
	height: 100%;
	background: linear-gradient(90deg, #667eea, #764ba2);
	transition: width 0.3s;
	border-radius: 4px;
}

.question-content {
	background: #f9f9f9;
	padding: 32px;
	border-radius: 12px;
	margin-bottom: 24px;
}

.question-content__text {
	font-family: Poppins, sans-serif;
	font-size: 22px;
	font-weight: 600;
	color: #333;
	margin: 0 0 20px 0;
	line-height: 1.5;
}

.question-content__image {
	margin-bottom: 20px;
	text-align: center;
}

.question-content__image img {
	max-width: 100%;
	max-height: 400px;
	border-radius: 8px;
}

.question-content__type {
	display: inline-block;
	padding: 8px 16px;
	background: #e3f2fd;
	color: #1976d2;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 600;
	margin-bottom: 24px;
}

.question-answers {
	margin-top: 24px;
}

.answer-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.answer-item {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 16px;
	background: #fff;
	border: 2px solid #e0e0e0;
	border-radius: 12px;
	cursor: pointer;
	transition: all 0.3s;
}

.answer-item:hover:not(.answer-item--disabled) {
	border-color: #667eea;
	box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
}

.answer-item--selected {
	border-color: #667eea;
	background: rgba(102, 126, 234, 0.1);
}

.answer-item__content {
	display: flex;
	align-items: center;
	gap: 12px;
}

.answer-item__radio,
.answer-item__checkbox {
	width: 20px;
	height: 20px;
	cursor: pointer;
	accent-color: #667eea;
	flex-shrink: 0;
}

.answer-item__marker {
	font-weight: 700;
	color: #667eea;
	flex-shrink: 0;
	font-size: 16px;
}

.answer-item__text {
	flex: 1;
	font-size: 16px;
	color: #333;
	line-height: 1.5;
}

.answer-item__image {
	margin-top: 12px;
	text-align: center;
}

.answer-item__image img {
	max-width: 100%;
	max-height: 200px;
	border-radius: 8px;
}

.answer-text-input__field {
	width: 100%;
	padding: 16px;
	font-size: 16px;
	border: 2px solid #e0e0e0;
	border-radius: 12px;
	outline: none;
	transition: all 0.3s;
}

.answer-text-input__field:focus {
	border-color: #667eea;
	box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.answer-text-input__field:disabled {
	background: #f5f5f5;
	cursor: not-allowed;
}

.question-navigation {
	display: flex;
	gap: 12px;
	justify-content: center;
}

/* Панель навигации */
.navigation-panel__title {
	margin: 0 0 20px 0;
	font-size: 18px;
	font-weight: 700;
	color: #333;
	text-align: center;
}

.navigation-panel__grid {
	display: grid;
	grid-template-columns: repeat(5, 1fr);
	gap: 8px;
	margin-bottom: 20px;
}

.nav-question-btn {
	aspect-ratio: 1;
	border: 2px solid #e0e0e0;
	background: #fff;
	border-radius: 8px;
	font-size: 16px;
	font-weight: 600;
	color: #666;
	cursor: pointer;
	transition: all 0.3s;
	position: relative;
}

.nav-question-btn:hover:not(:disabled) {
	border-color: #667eea;
	color: #667eea;
}

.nav-question-btn:disabled {
	cursor: not-allowed;
}

.nav-question-btn--current {
	border-color: #667eea;
	background: #667eea;
	color: #fff;
}

.nav-question-btn--answered {
	background: #e3f2fd;
	border-color: #90caf9;
}

.nav-question-btn--correct {
	background: #4caf50;
	border-color: #4caf50;
	color: #fff;
}

.nav-question-btn--wrong {
	background: #f44336;
	border-color: #f44336;
	color: #fff;
}

.navigation-panel__legend {
	padding: 16px;
	background: #fff;
	border-radius: 8px;
	margin-bottom: 16px;
}

.legend-item {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
	font-size: 13px;
}

.legend-item:last-child {
	margin-bottom: 0;
}

.legend-item__dot {
	width: 16px;
	height: 16px;
	border-radius: 4px;
	border: 2px solid #e0e0e0;
}

.legend-item__dot--current {
	background: #667eea;
	border-color: #667eea;
}

.legend-item__dot--answered {
	background: #e3f2fd;
	border-color: #90caf9;
}

.legend-item__dot--unanswered {
	background: #fff;
}

.navigation-panel__finish {
	width: 100%;
}

/* Кнопки */
.btn {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 12px 24px;
	border: none;
	border-radius: 8px;
	font-family: 'Poppins', sans-serif;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s;
	white-space: nowrap;
}

.btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.btn--primary {
	background: #667eea;
	color: #fff;
}

.btn--primary:hover:not(:disabled) {
	background: #5568d3;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn--secondary {
	background: #f0f0f0;
	color: #333;
}

.btn--secondary:hover:not(:disabled) {
	background: #e0e0e0;
}

.btn--success {
	background: #4caf50;
	color: #fff;
}

.btn--success:hover:not(:disabled) {
	background: #45a049;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
}

.btn--warning {
	background: #ff9800;
	color: #fff;
}

.btn--warning:hover:not(:disabled) {
	background: #fb8c00;
}

.btn--large {
	padding: 14px 32px;
	font-size: 16px;
}

/* Модальное окно результатов */
.results-modal {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.8);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 200000;
	padding: 20px;
	overflow-y: auto;
}

.results-modal__content {
	background: #fff;
	border-radius: 16px;
	max-width: 900px;
	width: 100%;
	max-height: 90vh;
	display: flex;
	flex-direction: column;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.results-modal__header {
	padding: 24px 32px;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	color: #fff;
	border-radius: 16px 16px 0 0;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.results-modal__title {
	margin: 0;
	font-size: 28px;
	font-weight: 700;
}

.results-modal__close {
	width: 40px;
	height: 40px;
	background: rgba(255, 255, 255, 0.2);
	border: none;
	border-radius: 50%;
	color: #fff;
	font-size: 24px;
	cursor: pointer;
	transition: all 0.2s;
}

.results-modal__close:hover {
	background: rgba(255, 255, 255, 0.3);
	transform: rotate(90deg);
}

.results-modal__body {
	flex: 1;
	padding: 32px;
	overflow-y: auto;
}

.results-summary {
	margin-bottom: 32px;
}

.results-summary__score {
	text-align: center;
	padding: 32px;
	background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
	border-radius: 12px;
	margin-bottom: 24px;
}

.results-summary__label {
	font-size: 18px;
	color: #666;
	margin-bottom: 12px;
}

.results-summary__value {
	font-size: 72px;
	font-weight: 700;
	margin-bottom: 8px;
}

.results-summary__value--success {
	color: #4caf50;
}

.results-summary__value--failure {
	color: #f44336;
}

.results-summary__points {
	font-size: 20px;
	color: #666;
}

.results-summary__stats {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 16px;
	margin-bottom: 24px;
}

.stat-card {
	text-align: center;
	padding: 24px;
	border-radius: 12px;
	color: #fff;
}

.stat-card--success {
	background: linear-gradient(135deg, #4caf50, #8bc34a);
}

.stat-card--danger {
	background: linear-gradient(135deg, #f44336, #e91e63);
}

.stat-card--neutral {
	background: linear-gradient(135deg, #9e9e9e, #757575);
}

.stat-card__icon {
	font-size: 32px;
	margin-bottom: 8px;
}

.stat-card__value {
	font-size: 36px;
	font-weight: 700;
	margin-bottom: 4px;
}

.stat-card__label {
	font-size: 14px;
	opacity: 0.9;
}

.results-summary__passing {
	text-align: center;
	padding: 20px;
	background: #f9f9f9;
	border-radius: 12px;
}

.results-summary__passing-label {
	font-size: 16px;
	color: #666;
	margin-bottom: 8px;
}

.results-summary__passing-status {
	font-size: 24px;
	font-weight: 700;
}

.results-summary__passing-status--pass {
	color: #4caf50;
}

.results-summary__passing-status--fail {
	color: #f44336;
}

.results-details__title {
	font-size: 20px;
	font-weight: 700;
	margin: 0 0 20px 0;
}

.results-details__list {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.result-item {
	padding: 20px;
	border-radius: 12px;
	border: 2px solid #e0e0e0;
}

.result-item--correct {
	border-color: #4caf50;
	background: #f1f8f4;
}

.result-item--wrong {
	border-color: #f44336;
	background: #fef5f5;
}

.result-item--unanswered {
	border-color: #9e9e9e;
	background: #fafafa;
}

.result-item__header {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 12px;
	flex-wrap: wrap;
}

.result-item__number {
	font-weight: 700;
	color: #333;
}

.result-item__status {
	flex: 1;
	font-weight: 600;
}

.result-item__points {
	font-weight: 700;
	color: #667eea;
}

.result-item__question {
	font-size: 16px;
	font-weight: 600;
	color: #333;
	margin-bottom: 12px;
}

.result-item__answers {
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-bottom: 12px;
}

.result-answer {
	padding: 12px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	gap: 8px;
	background: #fff;
}

.result-answer--correct {
	background: #e8f5e9;
	border: 1px solid #4caf50;
}

.result-answer--wrong {
	background: #ffebee;
	border: 1px solid #f44336;
}

.result-answer--user {
	font-weight: 600;
}

.result-answer__icon {
	width: 24px;
	font-size: 16px;
}

.result-item__text-answer {
	padding: 12px;
	background: #f9f9f9;
	border-radius: 8px;
	margin-bottom: 12px;
}

.text-answer-row {
	margin-bottom: 8px;
}

.text-answer-row:last-child {
	margin-bottom: 0;
}

.text-answer--correct {
	color: #4caf50;
	font-weight: 600;
}

.text-answer--wrong {
	color: #f44336;
	font-weight: 600;
}

.result-item__explanation {
	padding: 12px;
	background: #fff3e0;
	border-left: 4px solid #ff9800;
	border-radius: 4px;
	font-size: 14px;
}

.results-modal__footer {
	padding: 24px 32px;
	border-top: 1px solid #e0e0e0;
	display: flex;
	gap: 12px;
	justify-content: center;
}

.attempts-exhausted {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 16px;
	background: #ffebee;
	border-radius: 8px;
	color: #c62828;
}

.attempts-exhausted__icon {
	font-size: 20px;
}

.attempts-exhausted__text {
	font-weight: 500;
}

.mode-badge__attempts {
	margin-left: 4px;
	opacity: 0.8;
}

/* Адаптивность */
@media (max-width: 1200px) {
	.test-runner__sidebar {
		width: 250px;
	}
}

@media (max-width: 992px) {
	.test-runner__body {
		flex-direction: column;
	}

	.test-runner__sidebar {
		width: 100%;
		max-height: 200px;
	}

	.test-runner__sidebar--left {
		border-right: none;
		border-bottom: 1px solid #e0e0e0;
	}

	.test-runner__sidebar--right {
		border-left: none;
		border-top: 1px solid #e0e0e0;
	}

	.navigation-panel__grid {
		grid-template-columns: repeat(10, 1fr);
	}

	.results-summary__stats {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 768px) {
	.test-runner__container {
		inset: 0;
		width: 100vw;
		height: 100vh;
		border-radius: 0;
	}

	.test-runner__header {
		flex-direction: column;
		gap: 12px;
		padding: 16px;
	}

	.test-runner__timer {
		font-size: 16px;
	}

	.test-runner__main {
		padding: 16px;
	}

	.question-content {
		padding: 20px;
	}

	.results-summary__value {
		font-size: 48px;
	}

	.settings-badges {
		justify-content: center;
	}
}

/* Larger UI for specific desktop/laptop ranges (per request) */
@media (min-width: 769px) and (max-width: 1366px) {
	.test-runner__container {
		inset: 8px;
	}

	.test-runner__sidebar {
		width: 240px;
	}

	.test-runner__main {
		padding: 24px;
	}

	.question-container {
		max-width: 100%;
	}

	.question-progress {
		margin-bottom: 20px;
	}

	.question-counter {
		margin-bottom: 16px;
		padding: 10px;
	}

	.question-content {
		padding: 24px;
		margin-bottom: 16px;
	}

	.question-content__text {
		margin-bottom: 14px;
	}

	.question-content__type {
		margin-bottom: 16px;
	}

	.question-answers {
		margin-top: 16px;
	}

	.answer-list {
		gap: 10px;
	}

	.answer-item {
		padding: 14px;
	}

	.test-runner__title {
		font-size: 26px;
	}

	.test-runner__timer {
		font-size: 22px;
	}

	.question-content__text {
		font-size: 24px;
	}

	.answer-item__text {
		font-size: 17px;
	}

	.btn {
		font-size: 15px;
	}
}

@media (min-width: 1367px) and (max-width: 1600px) {
	.test-runner__container {
		inset: 10px;
	}

	.test-runner__sidebar {
		width: 340px;
	}

	.test-runner__main {
		padding: 32px;
	}

	.question-container {
		max-width: 100%;
	}

	.question-progress {
		margin-bottom: 24px;
	}

	.question-content {
		padding: 32px;
		margin-bottom: 18px;
	}

	.question-content__type {
		margin-bottom: 18px;
	}

	.answer-item {
		padding: 15px;
	}

	.test-runner__title {
		font-size: 28px;
	}

	.test-runner__timer {
		font-size: 24px;
	}

	.question-content {
		padding: 32px;
	}

	.question-content__text {
		font-size: 26px;
	}

	.answer-item__text {
		font-size: 18px;
	}

	.btn {
		font-size: 16px;
		padding: 14px 26px;
	}

	.nav-question-btn {
		font-size: 17px;
	}
}

@media (min-width: 1601px) and (max-width: 1800px) {
	.test-runner__container {
		inset: 12px;
	}

	.test-runner__sidebar {
		width: 360px;
	}

	.test-runner__main {
		padding: 36px;
	}

	.question-container {
		max-width: 100%;
	}

	.test-runner__title {
		font-size: 30px;
	}

	.test-runner__timer {
		font-size: 26px;
	}

	.question-content {
		padding: 36px;
		margin-bottom: 20px;
	}

	.question-progress {
		margin-bottom: 26px;
	}

	.answer-item {
		padding: 16px;
	}

	.question-content__text {
		font-size: 28px;
	}

	.answer-item__text {
		font-size: 19px;
	}

	.btn {
		font-size: 16px;
		padding: 14px 28px;
	}

	.nav-question-btn {
		font-size: 18px;
	}
}
</style>
